<div class="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-indigo-100 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
  <div class="sm:mx-auto sm:w-full sm:max-w-lg">
    <img class="mx-auto h-12 w-auto" src="https://storage.googleapis.com/monecocmsfiles/logo_moneco_full_01ed69a2e8.svg" alt="Moneco">
    
    <!-- Header Titles -->
    <div class="mt-6 text-center">
      <h2 *ngIf="mode === 'verify'" class="text-3xl font-extrabold text-gray-900">
        Two-Factor Authentication
      </h2>
      <h2 *ngIf="mode === 'enroll'" class="text-3xl font-extrabold text-gray-900">
        Set up Two-Factor Authentication
      </h2>
      <h2 *ngIf="mode === 'recovery'" class="text-3xl font-extrabold text-gray-900">
        Emergency Recovery Access
      </h2>

      <p *ngIf="mode === 'verify'" class="mt-2 text-sm text-gray-600">
        Enter the 6-digit security code from your authenticator app.
      </p>
      <p *ngIf="mode === 'enroll'" class="mt-2 text-sm text-gray-600">
        Protect your Moneco account with an authenticator app (e.g. Google Authenticator, 1Password).
      </p>
      <p *ngIf="mode === 'recovery'" class="mt-2 text-sm text-gray-600">
        Enter one of your unused backup recovery codes to log in.
      </p>
    </div>
  </div>

  <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-lg">
    <div class="bg-white py-8 px-6 shadow-xl rounded-xl border border-gray-100 sm:px-10">

      <!-- Error Alert -->
      <div *ngIf="errorMessage" class="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-md animate-fade-in-up">
        <div class="flex">
          <div class="shrink-0">
            <svg class="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div class="ml-3">
            <p class="text-sm text-red-700 font-medium">{{ errorMessage }}</p>
          </div>
        </div>
      </div>

      <!-- ================= MODE 1: VERIFY (Standard 2FA Challenge) ================= -->
      <div *ngIf="mode === 'verify'">
        <form (ngSubmit)="submitTotp()" class="space-y-6">
          <div>
            <label for="totp-code" class="block text-sm font-medium text-gray-700 text-center">
              Authenticator Code
            </label>
            <div class="mt-2">
              <input
                [(ngModel)]="totpCode"
                (ngModelChange)="onTotpCodeChange()"
                name="totpCode"
                id="totp-code"
                type="text"
                inputmode="numeric"
                pattern="[0-9]*"
                maxlength="6"
                autocomplete="one-time-code"
                autofocus
                placeholder="000000"
                class="block w-full text-center tracking-[0.6em] font-mono text-2xl py-3 rounded-lg border border-gray-300 shadow-sm placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-150"
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              [disabled]="loading || totpCode.trim().length !== 6"
              class="w-full flex justify-center items-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition duration-150 ease-in-out cursor-pointer"
            >
              <svg *ngIf="loading" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
              </svg>
              <span>{{ loading ? 'Verifying...' : 'Verify & Continue' }}</span>
            </button>
          </div>

          <div class="border-t border-gray-200 pt-4 flex flex-col items-center space-y-3 text-sm">
            <button
              type="button"
              (click)="setMode('recovery')"
              class="text-indigo-600 hover:text-indigo-500 font-medium transition cursor-pointer"
            >
              Can't access your authenticator app? Use a recovery code
            </button>
            <button
              type="button"
              (click)="backToLogin()"
              class="text-gray-500 hover:text-gray-700 transition cursor-pointer"
            >
              Back to sign in
            </button>
          </div>
        </form>
      </div>

      <!-- ================= MODE 2: ENROLL (First-time / Mandated Setup) ================= -->
      <div *ngIf="mode === 'enroll'" class="space-y-6">
        
        <!-- Loading State for Enrollment Info -->
        <div *ngIf="loading && !qrCodeDataUrl" class="py-12 text-center">
          <svg class="animate-spin mx-auto h-8 w-8 text-indigo-600" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
          </svg>
          <p class="mt-4 text-sm text-gray-600">Generating secure pairing keys...</p>
        </div>

        <div *ngIf="qrCodeDataUrl" class="space-y-6">
          <!-- Step 1: Scan QR Code -->
          <div>
            <div class="flex items-center space-x-2">
              <span class="flex items-center justify-center h-6 w-6 rounded-full bg-indigo-600 text-white text-xs font-bold">1</span>
              <h3 class="text-sm font-semibold text-gray-900">Scan this QR code with your authenticator app</h3>
            </div>

            <div class="mt-3 flex flex-col items-center justify-center p-4 bg-gray-50 rounded-xl border border-gray-200">
              <div class="bg-white p-3 rounded-lg shadow-sm border border-gray-100">
                <img [src]="qrCodeDataUrl" alt="TOTP QR Code" class="w-48 h-48 object-contain" />
              </div>

              <!-- Manual Secret Entry -->
              <div class="mt-4 w-full text-center">
                <p class="text-xs text-gray-500 mb-1">Cannot scan? Enter this secret manually:</p>
                <div class="flex items-center justify-center space-x-2 bg-white px-3 py-1.5 rounded border border-gray-300 font-mono text-xs text-gray-800 select-all">
                  <span class="truncate max-w-[220px]">{{ secret }}</span>
                  <button
                    type="button"
                    (click)="copySecret()"
                    class="text-indigo-600 hover:text-indigo-800 font-sans text-xs font-semibold px-2 py-0.5 rounded hover:bg-indigo-50 transition cursor-pointer"
                  >
                    {{ copiedSecret ? '✓ Copied' : 'Copy' }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 2: Emergency Recovery Codes with Mandatory Security Warning -->
          <div>
            <div class="flex items-center space-x-2">
              <span class="flex items-center justify-center h-6 w-6 rounded-full bg-indigo-600 text-white text-xs font-bold">2</span>
              <h3 class="text-sm font-semibold text-gray-900">Save your emergency recovery codes</h3>
            </div>

            <!-- Prominent Security Warning Alert -->
            <div class="mt-3 bg-amber-50 border-l-4 border-amber-400 p-3.5 rounded-r-md">
              <div class="flex">
                <div class="shrink-0">
                  <svg class="h-5 w-5 text-amber-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                  </svg>
                </div>
                <div class="ml-3">
                  <h4 class="text-xs font-bold text-amber-900 uppercase tracking-wide">Critical Security Warning</h4>
                  <p class="mt-1 text-xs text-amber-800 leading-relaxed">
                    Recovery codes are the <strong>only</strong> way to regain access if you lose your authenticator device.
                    <strong>Never share them</strong> with anyone to avoid account takeover. Store them immediately in a password manager or secure vault.
                  </p>
                </div>
              </div>
            </div>

            <!-- Recovery Codes List -->
            <div class="mt-3 bg-gray-50 p-4 rounded-xl border border-gray-200">
              <div class="grid grid-cols-2 gap-2 font-mono text-xs text-gray-800">
                <div *ngFor="let code of recoveryCodes; let i = index" class="bg-white p-2 rounded border border-gray-200 text-center font-medium shadow-xs">
                  <span class="text-gray-400 mr-1">{{ i + 1 }}.</span> {{ code }}
                </div>
              </div>

              <!-- Actions: Copy and Download -->
              <div class="mt-3 flex items-center justify-end space-x-2">
                <button
                  type="button"
                  (click)="copyRecoveryCodes()"
                  class="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-xs transition cursor-pointer"
                >
                  <svg class="h-4 w-4 mr-1 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                  {{ copiedRecoveryCodes ? '✓ All Copied' : 'Copy All Codes' }}
                </button>
                <button
                  type="button"
                  (click)="downloadRecoveryCodes()"
                  class="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-xs transition cursor-pointer"
                >
                  <svg class="h-4 w-4 mr-1 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download (.txt)
                </button>
              </div>

              <!-- Acknowledgment Checkbox -->
              <div class="mt-3 pt-3 border-t border-gray-200 flex items-start space-x-2">
                <input
                  id="saved-codes-checkbox"
                  type="checkbox"
                  [(ngModel)]="hasSavedRecoveryCodes"
                  class="h-4 w-4 mt-0.5 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer"
                />
                <label for="saved-codes-checkbox" class="text-xs text-gray-700 font-medium cursor-pointer">
                  I have safely saved my recovery codes in a secure location.
                </label>
              </div>
            </div>
          </div>

          <!-- Step 3: Enter 6-digit confirmation code -->
          <div>
            <div class="flex items-center space-x-2">
              <span class="flex items-center justify-center h-6 w-6 rounded-full bg-indigo-600 text-white text-xs font-bold">3</span>
              <h3 class="text-sm font-semibold text-gray-900">Confirm pairing with a 6-digit code</h3>
            </div>

            <div class="mt-3">
              <input
                [(ngModel)]="totpCode"
                (ngModelChange)="onTotpCodeChange()"
                name="enrollTotpCode"
                type="text"
                inputmode="numeric"
                pattern="[0-9]*"
                maxlength="6"
                placeholder="000000"
                class="block w-full text-center tracking-[0.6em] font-mono text-2xl py-3 rounded-lg border border-gray-300 shadow-sm placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-150"
              />
            </div>
          </div>

          <!-- Submit Button -->
          <div>
            <button
              type="button"
              (click)="submitTotp()"
              [disabled]="loading || totpCode.trim().length !== 6 || !hasSavedRecoveryCodes"
              class="w-full flex justify-center items-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition duration-150 ease-in-out cursor-pointer"
            >
              <svg *ngIf="loading" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
              </svg>
              <span>{{ loading ? 'Activating 2FA...' : 'Complete 2FA Setup' }}</span>
            </button>
          </div>

          <div class="text-center pt-2">
            <button
              type="button"
              (click)="backToLogin()"
              class="text-xs text-gray-500 hover:text-gray-700 transition cursor-pointer"
            >
              Cancel and return to sign in
            </button>
          </div>
        </div>
      </div>

      <!-- ================= MODE 3: RECOVERY CODE FALLBACK ================= -->
      <div *ngIf="mode === 'recovery'">
        <form (ngSubmit)="submitRecoveryCode()" class="space-y-6">
          <div>
            <label for="recovery-code" class="block text-sm font-medium text-gray-700 text-center">
              Emergency Recovery Code
            </label>
            <div class="mt-2">
              <input
                [(ngModel)]="recoveryCode"
                name="recoveryCode"
                id="recovery-code"
                type="text"
                autofocus
                placeholder="e.g. 12345678"
                class="block w-full text-center font-mono text-xl py-3 rounded-lg border border-gray-300 shadow-sm placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-150"
              />
            </div>
            <p class="mt-2 text-xs text-gray-500 text-center">
              Note: This recovery code will be permanently invalidated once used.
            </p>
          </div>

          <div>
            <button
              type="submit"
              [disabled]="loading || !recoveryCode.trim()"
              class="w-full flex justify-center items-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition duration-150 ease-in-out cursor-pointer"
            >
              <svg *ngIf="loading" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
              </svg>
              <span>{{ loading ? 'Authenticating...' : 'Sign In with Recovery Code' }}</span>
            </button>
          </div>

          <div class="border-t border-gray-200 pt-4 flex flex-col items-center space-y-3 text-sm">
            <button
              type="button"
              (click)="setMode('verify')"
              class="text-indigo-600 hover:text-indigo-500 font-medium transition cursor-pointer"
            >
              Back to Authenticator App code
            </button>
            <button
              type="button"
              (click)="backToLogin()"
              class="text-gray-500 hover:text-gray-700 transition cursor-pointer"
            >
              Back to sign in
            </button>
          </div>
        </form>
      </div>

    </div>
  </div>
</div>
